import React, { useState } from 'react';
import { Box, CircularProgress, Switch, TextField, Typography } from '@mui/material';
import { Camera, CameraConfig } from '../types';
import { updateCameraSetting } from '../frigateApi';
import { useLanguage } from '../i18n/LanguageContext';

interface Props {
  camera: Camera;
  gatewayUrl: string;
  recordDuration: number;
  onRecordDurationChange: (val: number) => void;
  onUpdate: (camera: Camera) => void;
}

type Section = 'record' | 'motion' | 'detect' | 'snapshots';

interface Setting {
  section: Section;
  label: string;
  value: boolean;
}

export const CameraSettingsPanel: React.FC<Props> = ({
  camera,
  gatewayUrl,
  recordDuration,
  onRecordDurationChange,
  onUpdate,
}) => {
  const { t } = useLanguage();
  const [loadingKey, setLoadingKey] = useState<Section | null>(null);

  const settings: Setting[] = [
    { section: 'motion',    label: t.cameraSettingMotion,    value: camera.config.motion?.enabled    ?? false },
    { section: 'detect',    label: t.cameraSettingDetect,    value: camera.config.detect?.enabled    ?? false },
    { section: 'snapshots', label: t.cameraSettingSnapshots, value: camera.config.snapshots?.enabled ?? false },
  ];

  const toggle = async (section: Section, current: boolean) => {
    setLoadingKey(section);
    try {
      await updateCameraSetting(gatewayUrl, camera.name, `${section}.enabled`, !current);
      const updatedConfig: CameraConfig = {
        ...camera.config,
        [section]: { ...(camera.config[section] as object), enabled: !current },
      };
      onUpdate({ ...camera, config: updatedConfig });
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body2">{t.cameraRecordDuration}</Typography>
        <TextField
          type="number"
          size="small"
          value={recordDuration}
          onChange={e => {
            const v = parseInt(e.target.value);
            if (!isNaN(v) && v >= 1) onRecordDurationChange(v);
          }}
          inputProps={{ min: 1, max: 300 }}
          sx={{ width: 80 }}
        />
      </Box>

      {settings.map(({ section, label, value }) => (
        <Box key={section} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2">{label}</Typography>
          {loadingKey === section ? (
            <CircularProgress size={22} />
          ) : (
            <Switch
              checked={value}
              onChange={() => toggle(section, value)}
              size="small"
            />
          )}
        </Box>
      ))}
    </Box>
  );
};
